import React, { useState } from 'react';
import { InternalFormSignerStep } from '@dill/dill-shared';
import { ArrowDown, ArrowUp, Edit2, X } from 'react-feather';
import { DillButton } from '../../../general/Dill';
import InternalFormSignerModal, {
  SIGNER_HOUSE_REP_OPTIONS,
  SIGNER_SOURCE_OPTIONS,
} from './InternalFormSignerModal';

// One line describing who signs and when, e.g. "Region RSM · Only when the sales rep is not a
// House Rep · if include-name-change-or-new-choice is Yes".
const describeSigner = (signer: InternalFormSignerStep) => {
  const source =
    signer.source === 'EMAILS'
      ? signer.emails.join(', ') || 'Specific emails'
      : SIGNER_SOURCE_OPTIONS.find((option) => option.value === signer.source)?.label ??
        signer.source;
  const houseRep =
    signer.houseRepRequirement && signer.houseRepRequirement !== 'ANY'
      ? SIGNER_HOUSE_REP_OPTIONS.find((option) => option.value === signer.houseRepRequirement)
          ?.label
      : '';
  const condition = signer.condition?.sourceFieldId
    ? `if ${signer.condition.sourceFieldId} is ${signer.condition.sourceOptionValues.join(' or ')}`
    : '';
  const fills = (signer.fields ?? []).length
    ? `fills in ${(signer.fields ?? []).map((field) => field.name).join(', ')}`
    : '';
  return [source, houseRep, condition, fills].filter(Boolean).join(' · ');
};

// INTERNAL_FORM templates only: who signs after the person who fills the form in, in order. Each
// step is emailed once the one before it signs (see internalFormSignerService.ts on the backend).
// Signers are added and edited in InternalFormSignerModal.
const InternalFormSignersSection = ({
  signers,
  onChange,
}: {
  signers: InternalFormSignerStep[];
  onChange: (signers: InternalFormSignerStep[]) => void;
}) => {
  // null: closed; 'new': adding; otherwise the index being edited.
  const [editing, setEditing] = useState<number | 'new' | null>(null);

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= signers.length) return;
    const next = [...signers];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const editingSigner = typeof editing === 'number' ? signers[editing] ?? null : null;

  return (
    <div className="border rounded p-2 mt-2 flex flex-col gap-2">
      <InternalFormSignerModal
        open={editing !== null}
        signer={editingSigner}
        signerNumber={(typeof editing === 'number' ? editing : signers.length) + 2}
        onClose={() => setEditing(null)}
        onSave={(saved) => {
          onChange(
            typeof editing === 'number'
              ? signers.map((signer, index) => (index === editing ? saved : signer))
              : [...signers, saved]
          );
          setEditing(null);
        }}
      />
      <span className="text-xs font-semibold">Signers</span>
      <p className="text-xs text-gray-400">
        Signer 1 is whoever fills in the form. Each signer below is emailed in order once the one
        before them signs, and can change answers before signing or decline. A signer whose
        conditions don&apos;t match the answers is skipped. A signer can have their own fields to
        fill in (e.g. Internal Information), which aren&apos;t part of the form.
      </p>
      {signers.map((signer, index) => (
        <div
          key={signer.id}
          className="border border-gray-200 rounded p-2 flex items-start justify-between gap-2">
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-semibold text-gray-700">
              {`Signer ${index + 2}${signer.label ? ` · ${signer.label}` : ''}`}
            </span>
            <span className="text-xs text-gray-500 break-words">{describeSigner(signer)}</span>
          </div>
          <div className="flex gap-1 shrink-0">
            {index > 0 && (
              <DillButton
                variant="icon-default"
                height="18px"
                width="18px"
                icon={<ArrowUp size={10} />}
                onClick={() => move(index, -1)}
              />
            )}
            {index < signers.length - 1 && (
              <DillButton
                variant="icon-default"
                height="18px"
                width="18px"
                icon={<ArrowDown size={10} />}
                onClick={() => move(index, 1)}
              />
            )}
            <DillButton
              variant="icon-default"
              height="18px"
              width="18px"
              icon={<Edit2 size={10} />}
              onClick={() => setEditing(index)}
            />
            <DillButton
              variant="icon-default"
              height="18px"
              width="18px"
              icon={<X size={10} />}
              onClick={() => onChange(signers.filter((_, i) => i !== index))}
            />
          </div>
        </div>
      ))}
      <DillButton label="Add Signer" variant="secondary" onClick={() => setEditing('new')} />
    </div>
  );
};

export default InternalFormSignersSection;
